import { api } from "./client";
import type { components } from "./types";

export type PersonProfile = components["schemas"]["PersonProfile"];
export type PersonWorkType = components["schemas"]["PersonWorkType"];
export type PersonScheduleEvent = components["schemas"]["PersonScheduleEvent"];
export type PersonEventType = components["schemas"]["PersonEventType"];
export type PersonScheduleView = components["schemas"]["PersonDetail"];
export type PersonSchedulePayload = components["schemas"]["PersonSchedulePayload"];
export type PersonRead = components["schemas"]["PersonRead"];
export type PersonActionCapabilities = components["schemas"]["PersonActionCapabilities"];
export type ActionCapability = components["schemas"]["ActionCapability"];
export type ActionReasonCode = components["schemas"]["ActionReasonCode"];
export type PeopleOrganization = components["schemas"]["PeopleOrganization"];
export type PeopleOrganizationUnit = components["schemas"]["PeopleOrganizationUnit"];
export type PeopleAvailability = components["schemas"]["AvailabilityResponse"];
export type PeopleAvailabilityRow = components["schemas"]["AvailabilityRow"];
export type PeopleAvailabilitySegment = components["schemas"]["AvailabilitySegment"];
export type User = components["schemas"]["User"];
export type PersonCreateRequest = components["schemas"]["PersonCreateRequest"];
export type PersonCreateResponse = components["schemas"]["PersonCreateResponse"];
export type PersonPatchRequest = components["schemas"]["PersonPatchRequest"];
export interface PersonResetPasswordResponse {
  user: User;
  temporary_password: string;
}

export type PeopleStatusFilter = "active" | "inactive" | "all";
export interface PeopleFilters {
  q?: string;
  unit_ids?: number[];
  include_subtree?: boolean;
  position_ids?: number[];
  status: PeopleStatusFilter;
}

export function listPeople(filters: PeopleFilters): Promise<PersonRead[]> {
  const params = new URLSearchParams();
  const q = filters.q?.trim();
  if (q) params.set("q", q);
  for (const unitId of [...new Set(filters.unit_ids ?? [])].sort((a, b) => a - b)) {
    params.append("unit_ids", String(unitId));
  }
  if (filters.include_subtree) params.set("include_subtree", "true");
  for (const positionId of [...new Set(filters.position_ids ?? [])].sort((a, b) => a - b)) {
    params.append("position_ids", String(positionId));
  }
  params.set("status", filters.status);
  return api.get<PersonRead[]>(`/people?${params.toString()}`);
}

export function createPerson(body: PersonCreateRequest): Promise<PersonCreateResponse> {
  return api.post<PersonCreateResponse>("/people", body);
}

export function patchPerson(id: number, body: PersonPatchRequest): Promise<User> {
  return api.patch<User>(`/people/${id}`, body);
}

export function deactivatePerson(id: number): Promise<User> {
  return api.post<User>(`/people/${id}/deactivate`, {});
}

export function reactivatePerson(id: number): Promise<User> {
  return api.post<User>(`/people/${id}/reactivate`, {});
}

export function resetPersonPin(id: number, pin: string): Promise<User> {
  return api.post<User>(`/people/${id}/reset-pin`, { pin });
}

export function resetPersonPassword(id: number): Promise<PersonResetPasswordResponse> {
  return api.post<PersonResetPasswordResponse>(`/people/${id}/reset-password`, {});
}

export function getPeopleOrganization(): Promise<PeopleOrganization> {
  return api.get<PeopleOrganization>("/people/organization");
}

export interface PeopleAvailabilityParams {
  from: string;
  to: string;
  status: PeopleStatusFilter;
  unit_id?: number;
  include_subtree?: boolean;
}

export function getPeopleAvailability(
  params: PeopleAvailabilityParams,
): Promise<PeopleAvailability> {
  const query = new URLSearchParams({ from: params.from, to: params.to });
  query.set("status", params.status);
  if (params.unit_id != null) query.set("unit_id", String(params.unit_id));
  if (params.include_subtree) query.set("include_subtree", "true");
  return api.get<PeopleAvailability>(`/people/availability?${query.toString()}`);
}

export function getPerson(id: number): Promise<PersonScheduleView> {
  return api.get<PersonScheduleView>(`/people/${id}`);
}

export function listPersonEventTypes(): Promise<PersonEventType[]> {
  return api.get<PersonEventType[]>("/people/event-types");
}

export function createScheduleEvent(
  userId: number,
  body: PersonSchedulePayload,
): Promise<PersonScheduleEvent> {
  return api.post<PersonScheduleEvent>(`/people/${userId}/schedule`, body);
}

export function updateScheduleEvent(
  userId: number,
  eventId: number,
  body: PersonSchedulePayload,
): Promise<PersonScheduleEvent> {
  return api.patch<PersonScheduleEvent>(`/people/${userId}/schedule/${eventId}`, body);
}

export function deleteScheduleEvent(userId: number, eventId: number): Promise<void> {
  return api.delete<void>(`/people/${userId}/schedule/${eventId}`);
}

/**
 * Заменить набор работ «сверх должности» (задача 123 §2.2, replace-all).
 *
 * Работы должности сюда не шлём — сервер всё равно вычитает их из запроса,
 * `user_work_types` хранит только ad-hoc. Ответ — сохранённый набор ad-hoc.
 */
export function putPersonWorkTypeIds(userId: number, ids: number[]): Promise<number[]> {
  return api.put<number[]>(`/people/${userId}/work-types`, { ids });
}
